import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ScrollToTop from "@/components/layout/ScrollToTop";
import ContactForm from "@/components/ContactForm";
import motion from "@/lib/lazy-motion-tags";
import { Helmet } from "react-helmet";
import IconLoader from "@/components/IconLoader";
import { Card } from "@/components/ui/card";
import { trackWhatsAppClick, trackPhoneCall } from "@/lib/analytics";

const Contact = () => {
  const contactInfo = [
    {
      icon: "Phone",
      title: "اتصل بنا",
      details: ["+966 55 235 1116"],
      link: "tel:+966552351116",
    },
    {
      icon: "Mail",
      title: "البريد الإلكتروني",
      details: ["info@newbusiness.com"],
      link: "mailto:info@newbusiness.com",
    },
    {
      icon: "MapPin",
      title: "العنوان",
      details: ["حي طويق - صناعية طويق", "الرياض، المملكة العربية السعودية"],
    },
    {
      icon: "Clock",
      title: "ساعات العمل",
      details: ["السبت - الخميس: 8 صباحاً - 6 مساءً", "الجمعة: مغلق"],
    },
  ];

  return (
    <>
      <ScrollToTop />
      <Helmet>
        <title>تواصل معنا - New Business | احصل على عرض سعر مجاني</title>
        <meta name="description" content="تواصل مع New Business للحصول على استشارة مجانية وعرض سعر فوري لأبواب WPC. نحن في خدمتك" />
      </Helmet>
      
      <div className="min-h-screen">
        <Navbar />
        
        {/* Hero */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-background via-muted/30 to-background">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="text-gradient">تواصل معنا</span>
              </h1>
              <p className="text-xl text-muted-foreground">
                نحن هنا لمساعدتك في اختيار الأبواب المثالية لمشروعك
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    dir="ltr"
                  >
                    <Card className="p-8 text-center hover-lift h-full">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center mb-6 mx-auto shadow-elegant">
                              <IconLoader name={info.icon} className="h-8 w-8 text-accent" />
                            </div>
                      <h3 className="text-xl font-bold mb-4">{info.title}</h3>
                      {info.link ? (
                        <a
                          href={info.link}
                          onClick={() => {
                            if (info.icon === "Phone") trackPhoneCall("contact_page_card");
                          }}
                          className="block"
                        >
                          {info.details.map((detail, idx) => (
                            <p key={idx} className="text-muted-foreground hover:text-primary transition-colors">
                              {detail}
                            </p>
                          ))}
                        </a>
                      ) : (
                        info.details.map((detail, idx) => (
                          <p key={idx} className="text-muted-foreground">
                            {detail}
                          </p>
                        ))
                      )}
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Contact Form & Map */}
        <section className="py-24 bg-card">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              {/* Form */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl font-bold mb-6">احصل على عرض سعر مجاني</h2>
                <p className="text-lg text-muted-foreground mb-8">
                  املأ النموذج أدناه وسنتواصل معك في أقرب وقت ممكن
                </p>
                <Card className="p-8 shadow-elegant">
                  <ContactForm />
                </Card>
              </motion.div>

              {/* Map & Social */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="space-y-8"
              >
                <div>
                  <h3 className="text-2xl font-bold mb-6">موقعنا على الخريطة</h3>
                  <div className="rounded-2xl overflow-hidden shadow-elegant h-80">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3628.4940920802383!2d46.51136411022047!3d24.572138878027353!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjTCsDM0JzE5LjciTiA0NsKwMzAnNTAuMiJF!5e0!3m2!1sen!2ssa!4v1762851831276!5m2!1sen!2ssa"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="موقع المصنع"
                    />
                  </div>
                </div>

                <Card className="p-8">
                  <h3 className="text-2xl font-bold mb-6">تابعنا على</h3>
                  <div className="flex gap-4">
                    <a
                      href="#"
                      aria-label="Instagram"
                      className="w-14 h-14 rounded-full bg-gradient-primary flex items-center justify-center hover:shadow-glow transition-all"
                    >
                      <IconLoader name="Instagram" className="h-6 w-6 text-accent" />
                    </a>
                    <a
                      href="#"
                      aria-label="Facebook"
                      className="w-14 h-14 rounded-full bg-gradient-primary flex items-center justify-center hover:shadow-glow transition-all"
                    >
                      <IconLoader name="Facebook" className="h-6 w-6 text-accent" />
                    </a>
                    <a
                      href="#"
                      aria-label="Twitter"
                      className="w-14 h-14 rounded-full bg-gradient-primary flex items-center justify-center hover:shadow-glow transition-all"
                    >
                      <IconLoader name="Twitter" className="h-6 w-6 text-accent" />
                    </a>
                  </div>
                </Card>

                <Card className="p-8 bg-gradient-primary text-accent">
                  <h3 className="text-2xl font-bold mb-4">هل تحتاج مساعدة فورية؟</h3>
                  <p className="mb-6">فريقنا جاهز للرد على استفساراتك</p>
                  <a
                    href="https://wa.me/966552351116"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackWhatsAppClick("contact_page_card")}
                    className="inline-block w-full bg-accent text-primary hover:bg-accent/90 transition-colors py-4 px-6 rounded-xl font-bold text-center"
                  >
                    تواصل عبر واتساب
                  </a>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Contact;
